import ReactDOMServer from 'react-dom/server';
import {
  dispose, EChartsOption, init,
} from 'echarts';
import {
  defaultChartOptions, defaultXAxisConfig, formatterXAxisLabel,
} from '../../constants';
import { RefObject } from 'react';
import { HealthTooltipGraph } from '../components/GraphTooltip';
import { DISABLED_DATA_INFO } from '../constants';
import { colors } from 'styles/colors';
import {
  colorByHealthVariant, hoverColorByHealthVariant,
} from '../helpers';
import { t } from 'i18next';
import { HealthVariant } from '../components/UnitsInfo/types';
import { GetUnitHealthHistory } from '../../types';
import { createPercentageYAxis, generateMonthYearXAxisNames } from '../../helpers/graph';

interface UseGenerateHealthGraphReturn {
  renderHealthChart: (data: GetUnitHealthHistory | undefined) => void;
}

interface TooltipParams {
  data: { value: number, units: number };
  name: string;
  seriesName: HealthVariant;
  value: number;
}

export function useGenerateHealthGraph(
  containerRef: RefObject<HTMLDivElement>,
  disabled?: boolean,
): UseGenerateHealthGraphReturn {
  function createChartOptions(seriesData: EChartsOption['series'], data: GetUnitHealthHistory): EChartsOption {
    const option: EChartsOption = {
      ...defaultChartOptions,
      tooltip: {
        ...defaultChartOptions.tooltip,
        trigger: 'item',
        show: !disabled,
        formatter: (value) => formatterTooltip(value),
        confine: true,
      },
      xAxis: createXAxis(data),
      yAxis: createPercentageYAxis(),
      series: seriesData,
    };

    return option;
  }

  function createXAxis(data: GetUnitHealthHistory): EChartsOption['xAxis'] {
    return {
      ...defaultXAxisConfig,
      data: generateMonthYearXAxisNames(data.MONTHLY_HISTORY),
      axisLabel: {
        ...defaultXAxisConfig.axisLabel,
        formatter: (value) => formatterXAxisLabel(value, 'month', disabled),
      },
    } as EChartsOption['xAxis'];
  }

  function formatterTooltip(value: TooltipParams): string {
    return ReactDOMServer.renderToString(
      <HealthTooltipGraph
        date={value.name}
        percentage={value.value}
        units={value.data.units}
        variant={value.seriesName}
      />,
    );
  }

  function renderHealthChart(dataInfo: GetUnitHealthHistory | undefined): (() => void) | void {
    if (!containerRef.current) return;

    const seriesData = buildChartData(dataInfo);

    dispose(containerRef.current);
    const chartInstance = init(containerRef.current, null, {
      width: 672,
    });

    chartInstance.setOption(createChartOptions(seriesData, dataInfo ?? DISABLED_DATA_INFO));

    const resizeHandler = () => {
      chartInstance.resize();
    };

    window.addEventListener('resize', resizeHandler);

    return (): void => {
      window.removeEventListener('resize', resizeHandler);
      chartInstance.dispose();
    };
  }

  return {
    renderHealthChart,
  };
}

function buildChartData(data: GetUnitHealthHistory | undefined): EChartsOption['series'] {
  if (!data) {
    return [
      {
        name: t('semDados'),
        stack: 'total',
        barWidth: '21px',
        type: 'bar',
        cursor: 'not-allowed',
        color: colors.icon['disabled-300'],
        data: DISABLED_DATA_INFO.MONTHLY_HISTORY.map((item) => item.PERCENTAGE_HEALTHY),
      },
    ];
  }

  const healthy = data.MONTHLY_HISTORY.map((item) => ({ units: item.COUNT_TOTAL_UNITS_HEALTHY, value: item.PERCENTAGE_HEALTHY }));
  const alert = data.MONTHLY_HISTORY.map((item) => ({ units: item.COUNT_TOTAL_UNITS_ALERT, value: item.PERCENTAGE_ALERT }));
  const critical = data.MONTHLY_HISTORY.map((item) => ({ units: item.COUNT_TOTAL_UNITS_CRITICAL, value: item.PERCENTAGE_CRITICAL }));
  const inoperative = data.MONTHLY_HISTORY.map((item) => ({ units: item.COUNT_TOTAL_UNITS_INOPERANT, value: item.PERCENTAGE_INOPERANT }));

  const seriesData = [
    inoperative,
    critical,
    alert,
    healthy,
  ];

  const seriesHealth: HealthVariant[] = [
    'inoperative',
    'critical',
    'alert',
    'healthy',
  ];

  const series = seriesHealth.map((name, index) => ({
    name,
    type: 'bar',
    stack: 'total',
    barWidth: '12px',
    data: seriesData[index],
    color: colorByHealthVariant(name),
    emphasis: {
      focus: 'self',
      blurScope: 'coordinateSystem',
      itemStyle: {
        color: hoverColorByHealthVariant(name),
      },
    },
    blur: {
      itemStyle: {
        color: colorByHealthVariant(name),
        opacity: 1,
      },
    },
  }));

  return series as EChartsOption['series'];
}
